// exp_PORTFOLIO-61587_file
import { DefaultRootState } from 'react-redux';

import { Nullable } from 'types';
import {
    BASE_VACANCY_OF_THE_DAY_PRESETS,
    EMPTY_VACANCY_OF_THE_DAY_ERRORS,
    VacancyOfTheDayErrors,
    VacancyOfTheDayPresetId,
    VacancyOfTheDayPresets,
} from 'utils/vacancyOfTheDay';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export interface VacancyOfTheDayVacancy {
    vacancyId: Nullable<number>;
    vacancyName: Nullable<string>;
    vacancyUrl: Nullable<string>;
    areaId: Nullable<number>;
}

export interface VacancyOfTheDayForm {
    vacancy: VacancyOfTheDayVacancy;
    areas: number[];
    roles: number[];
    presets: VacancyOfTheDayPresets;
    preset: VacancyOfTheDayPresetId;
    clicks: number;
    budget: number;
}

const SET_VACANCY_OF_THE_DAY_FORM = 'SET_VACANCY_OF_THE_DAY_FORM';
const SET_VACANCY_OF_THE_DAY_AREAS = 'SET_VACANCY_OF_THE_DAY_AREAS';
const SET_VACANCY_OF_THE_DAY_ROLES = 'SET_VACANCY_OF_THE_DAY_ROLES';
const SET_VACANCY_OF_THE_DAY_PRESET = 'SET_VACANCY_OF_THE_DAY_PRESET';
const SET_VACANCY_OF_THE_DAY_CLICKS = 'SET_VACANCY_OF_THE_DAY_CLICKS';
const SET_VACANCY_OF_THE_DAY_BUDGET = 'SET_VACANCY_OF_THE_DAY_BUDGET';
const SET_VACANCY_OF_THE_DAY_ERRORS = 'SET_VACANCY_OF_THE_DAY_ERRORS';
const SET_VACANCY_OF_THE_DAY_MODAL_VISIBLE = 'SET_VACANCY_OF_THE_DAY_MODAL_VISIBLE';

interface PayloadTypes {
    [SET_VACANCY_OF_THE_DAY_FORM]: VacancyOfTheDayForm;
    [SET_VACANCY_OF_THE_DAY_AREAS]: number[];
    [SET_VACANCY_OF_THE_DAY_ROLES]: number[];
    [SET_VACANCY_OF_THE_DAY_PRESET]: VacancyOfTheDayPresetId;
    [SET_VACANCY_OF_THE_DAY_CLICKS]: number;
    [SET_VACANCY_OF_THE_DAY_BUDGET]: number;
    [SET_VACANCY_OF_THE_DAY_ERRORS]: Partial<VacancyOfTheDayErrors>;
    [SET_VACANCY_OF_THE_DAY_MODAL_VISIBLE]: boolean;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setVacancyOfTheDayForm = ActionCreator(SET_VACANCY_OF_THE_DAY_FORM);
export const setVacancyOfTheDayAreas = ActionCreator(SET_VACANCY_OF_THE_DAY_AREAS);
export const setVacancyOfTheDayRoles = ActionCreator(SET_VACANCY_OF_THE_DAY_ROLES);
export const setVacancyOfTheDayPreset = ActionCreator(SET_VACANCY_OF_THE_DAY_PRESET);
export const setVacancyOfTheDayClicks = ActionCreator(SET_VACANCY_OF_THE_DAY_CLICKS);
export const setVacancyOfTheDayBudget = ActionCreator(SET_VACANCY_OF_THE_DAY_BUDGET);
export const setVacancyOfTheDayErrors = ActionCreator(SET_VACANCY_OF_THE_DAY_ERRORS);
export const setVacancyOfTheDayModalVisible = ActionCreator(SET_VACANCY_OF_THE_DAY_MODAL_VISIBLE);

interface VacancyOfTheDayState extends VacancyOfTheDayForm {
    errors: VacancyOfTheDayErrors;
    unavailableModalVisible: boolean;
    initialAreas: number[];
    initialRoles: number[];
}

const VACANCY_OF_THE_DAY_INITIAL_STATE: VacancyOfTheDayState = {
    vacancy: {
        vacancyId: null,
        vacancyName: null,
        vacancyUrl: null,
        areaId: null,
    },
    areas: [],
    roles: [],
    presets: BASE_VACANCY_OF_THE_DAY_PRESETS,
    preset: VacancyOfTheDayPresetId.Medium,
    clicks: BASE_VACANCY_OF_THE_DAY_PRESETS.MEDIUM.clicks,
    budget: BASE_VACANCY_OF_THE_DAY_PRESETS.MEDIUM.budget,
    errors: EMPTY_VACANCY_OF_THE_DAY_ERRORS,
    unavailableModalVisible: false,
    initialAreas: [],
    initialRoles: [],
};

export default createReducer<VacancyOfTheDayState, PayloadTypes>(VACANCY_OF_THE_DAY_INITIAL_STATE, {
    [SET_VACANCY_OF_THE_DAY_FORM]: (state, { payload }) => ({
        ...state,
        ...payload,
        errors: EMPTY_VACANCY_OF_THE_DAY_ERRORS,
        unavailableModalVisible: false,
        initialAreas: payload.areas,
        initialRoles: payload.roles,
    }),
    [SET_VACANCY_OF_THE_DAY_AREAS]: (state, { payload }) => ({ ...state, areas: payload }),
    [SET_VACANCY_OF_THE_DAY_ROLES]: (state, { payload }) => ({ ...state, roles: payload }),
    [SET_VACANCY_OF_THE_DAY_PRESET]: (state, { payload }) => ({ ...state, preset: payload }),
    [SET_VACANCY_OF_THE_DAY_CLICKS]: (state, { payload }) => ({ ...state, clicks: payload }),
    [SET_VACANCY_OF_THE_DAY_BUDGET]: (state, { payload }) => ({ ...state, budget: payload }),
    [SET_VACANCY_OF_THE_DAY_ERRORS]: (state, { payload }) => ({
        ...state,
        errors: { ...state.errors, ...payload },
    }),
    [SET_VACANCY_OF_THE_DAY_MODAL_VISIBLE]: (state, { payload }) => ({
        ...state,
        unavailableModalVisible: payload,
    }),
});

export const selectVacancyOfTheDay = (state: DefaultRootState): VacancyOfTheDayState => state.vacancyOfTheDay;
export const selectVacancyOfTheDayVacancy = (state: DefaultRootState): VacancyOfTheDayVacancy =>
    state.vacancyOfTheDay.vacancy;
export const selectVacancyOfTheDayAreas = (state: DefaultRootState): number[] => state.vacancyOfTheDay.areas;
export const selectVacancyOfTheDayRoles = (state: DefaultRootState): number[] => state.vacancyOfTheDay.roles;
export const selectVacancyOfTheDayPresets = (state: DefaultRootState): VacancyOfTheDayPresets =>
    state.vacancyOfTheDay.presets;
export const selectVacancyOfTheDayPreset = (state: DefaultRootState): VacancyOfTheDayPresetId =>
    state.vacancyOfTheDay.preset;
export const selectVacancyOfTheDayClicks = (state: DefaultRootState): number => state.vacancyOfTheDay.clicks;
export const selectVacancyOfTheDayBudget = (state: DefaultRootState): number => state.vacancyOfTheDay.budget;
export const selectVacancyOfTheDayErrors = (state: DefaultRootState): VacancyOfTheDayErrors =>
    state.vacancyOfTheDay.errors;
export const selectVacancyOfTheDayModalVisible = (state: DefaultRootState): boolean =>
    state.vacancyOfTheDay.unavailableModalVisible;
export const selectVacancyOfTheDayInitialAreas = (state: DefaultRootState): number[] =>
    state.vacancyOfTheDay.initialAreas;
export const selectVacancyOfTheDayInitialRoles = (state: DefaultRootState): number[] =>
    state.vacancyOfTheDay.initialRoles;
